/**
 * The family plan as the app words it: days left in the trial, the price, and
 * which notice the log shows. Pure, so tests/plan.test.ts pins it. The server
 * decides the status (services/plan-rules.ts); this only reads it.
 */
import { toDate } from "./time.ts";

export interface PlanLike {
  status: "free" | "trial" | "paid" | "expired";
  trialEndsAt: Date | string | null;
  tier: "founding" | "regular" | "grandfathered" | null;
  offer: { tier: "founding" | "regular"; priceCents: number } | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** The log starts mentioning the trial this many days before it ends. */
export const TRIAL_REMINDER_DAYS = 3;

/** Whole days left, rounded up (13.2 days reads "14 days"); 0 once ended; null before setup. */
export function trialDaysLeft(plan: PlanLike, now: Date): number | null {
  if (!plan.trialEndsAt) return null;
  return Math.max(0, Math.ceil((toDate(plan.trialEndsAt).getTime() - now.getTime()) / DAY_MS));
}

/** "$1.99" */
export function priceLabel(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** The price after the founding families (`PRICE_CENTS.regular` on the server). */
export const REGULAR_PRICE_CENTS = 499;

type Offer = NonNullable<PlanLike["offer"]>;

/** The Buy button: "Buy at the founding price, $1.99", or "Buy for $4.99" after them. */
export function buyLabel(offer: Offer): string {
  const price = priceLabel(offer.priceCents);
  return offer.tier === "founding" ? `Buy at the founding price, ${price}` : `Buy for ${price}`;
}

/** Says the founding price won't last, while it applies; null after the first 100 families. */
export function foundingNote(offer: Offer | null): string | null {
  if (offer?.tier !== "founding") return null;
  return `Founding price: ${priceLabel(offer.priceCents)} once, ${priceLabel(REGULAR_PRICE_CENTS)} after the first 100 families.`;
}

/**
 * What the log shows above its actions: nothing, a reminder in the trial's
 * last days, or the read-only notice (which replaces the actions).
 */
export function logNotice(plan: PlanLike | undefined, now: Date): "none" | "ending" | "ended" {
  if (!plan) return "none";
  if (plan.status === "expired") return "ended";
  if (plan.status !== "trial") return "none";
  const left = trialDaysLeft(plan, now);
  return left !== null && left <= TRIAL_REMINDER_DAYS ? "ending" : "none";
}

/** Whether taps may write entries; the server refuses them after an unpaid trial. */
export const canLog = (plan: PlanLike | undefined): boolean => plan?.status !== "expired";
